/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0px 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum EnquiryActivityPeriodEnum {
    LAST_FINANCIAL_YEAR,
    LAST_MONTH,
    PREVIOUS_MONTH,
    PREVIOUS_FINANCIAL_YEAR,
}

interface EnquiryActivityByPeriodChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    options: EnquiryActivityPeriodEnum;
    groups: string[];
    newEnquiries: number[];
    followups: number[];
    currentNewEnquiriesTotal: number;
    previousNewEnquiriesTotal: number;
    currentFollowupsTotal: number;
    previousFollowupsTotal: number;
    periodString: string;
}

export const EnquiryActivityByPeriodChart: React.FC<EnquiryActivityByPeriodChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const enquiryActivityQuery = useQuery<EnquiryActivityByPeriodQueryData>(EnquiryActivityByPeriodQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: EnquiryActivityPeriodEnum.LAST_MONTH,
        groups: [],
        newEnquiries: [],
        followups: [],
        currentNewEnquiriesTotal: 0,
        previousNewEnquiriesTotal: 0,
        currentFollowupsTotal: 0,
        previousFollowupsTotal: 0,
        periodString: 'Financial Year',
    });

    useEffect(() => {
        setState((prevState) => {
            let groups: string[] = [];
            let newEnquiries: number[] = [];
            let followups: number[] = [];
            let currentNewEnquiriesTotal: number = 0;
            let previousNewEnquiriesTotal: number = 0;
            let currentFollowupsTotal: number = 0;
            let previousFollowupsTotal: number = 0;
            let periodString: string = 'Financial Year';

            if (!enquiryActivityQuery.error && enquiryActivityQuery.data) {
                const byPeriod = enquiryActivityQuery.data.statistics.lead.enquiryActivityByPeriod;

                let periodData: EnquiryActivityPeriod | null = null;

                if (prevState.options === EnquiryActivityPeriodEnum.LAST_FINANCIAL_YEAR) {
                    periodData = byPeriod.lastFinancialYear;
                    periodString = 'Financial Year';
                } else if (prevState.options === EnquiryActivityPeriodEnum.LAST_MONTH) {
                    periodData = byPeriod.lastMonth;
                    periodString = 'This Month';
                } else if (prevState.options === EnquiryActivityPeriodEnum.PREVIOUS_MONTH) {
                    periodData = byPeriod.previousMonth;
                    periodString = 'Previous Month';
                } else {
                    periodData = byPeriod.previousFinancialYear;
                    periodString = 'Previous Financial Year';
                }

                if (periodData) {
                    groups = periodData.enquiryActivityGrouping.map((s) => s.group);
                    newEnquiries = periodData.enquiryActivityGrouping.map((s) => s.newEnquiries);
                    followups = periodData.enquiryActivityGrouping.map((s) => s.followups);
                    currentNewEnquiriesTotal = periodData.summary.lastPeriodNewEnquiries;
                    previousNewEnquiriesTotal = periodData.summary.previousPeriodNewEnquiries;
                    currentFollowupsTotal = periodData.summary.lastPeriodFollowups;
                    previousFollowupsTotal = periodData.summary.previousPeriodFollowups;
                }
            }

            return {
                ...prevState,
                groups,
                newEnquiries,
                followups,
                currentNewEnquiriesTotal,
                previousNewEnquiriesTotal,
                currentFollowupsTotal,
                previousFollowupsTotal,
                periodString,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, enquiryActivityQuery.loading, enquiryActivityQuery.error, enquiryActivityQuery.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isEnquiryActivityByPeriodChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (enquiryActivityQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }

    const allCounts = [...state.newEnquiries, ...state.followups];

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.newEnquiries, state.followups) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<PeopleOutlinedIcon />} title={<>Enquiry Activity - {state.periodString}</>} />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">New Enquiries</div>
                        <div className="value">
                            <AnimatedNumber value={state.currentNewEnquiriesTotal} format={(amount) => formatNumber(amount)} />
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">Follow-ups</div>
                        <div className="value">
                            <AnimatedNumber value={state.currentFollowupsTotal} format={(amount) => formatNumber(amount)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        // No drilldowns yet
                    }}
                    data={{
                        labels: getLabels(state.groups),
                        datasets: [
                            {
                                fill: false,
                                label: 'New Enquiries',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.newEnquiries,
                                type: 'bar',
                            },
                            {
                                fill: false,
                                label: 'Follow-ups',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft, true),
                                data: state.followups,
                                type: 'bar',
                            },
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(allCounts),
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(allCounts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: string | number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue as number)}`;
                                    },
                                },
                                stacked: true
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: '',
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const { formattedValue, dataset } = context;
                                        const currentValue = parseInt(formattedValue.replace(/,/g, ''), 10);
                                        return `${dataset.label}: ${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.newEnquiries, state.followups) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="enquiryPeriod" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel
                        value={EnquiryActivityPeriodEnum.LAST_MONTH}
                        control={<Radio color="primary" />}
                        label="This Month"
                    />
                    <FormControlLabel
                        value={EnquiryActivityPeriodEnum.PREVIOUS_MONTH}
                        control={<Radio color="primary" />}
                        label="Previous Month"
                    />
                    <FormControlLabel
                        value={EnquiryActivityPeriodEnum.LAST_FINANCIAL_YEAR}
                        control={<Radio color="primary" />}
                        label="Financial Year"
                    />
                    <FormControlLabel
                        value={EnquiryActivityPeriodEnum.PREVIOUS_FINANCIAL_YEAR}
                        control={<Radio color="primary" />}
                        label="Previous Financial Year"
                    />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface EnquiryActivityByPeriodQueryData {
    statistics: Statistics;
}

export interface Statistics {
    lead: Lead;
}

export interface Lead {
    enquiryActivityByPeriod: EnquiryActivityByPeriod;
}

export interface EnquiryActivityByPeriod {
    lastFinancialYear: EnquiryActivityPeriod;
    lastMonth: EnquiryActivityPeriod;
    previousMonth: EnquiryActivityPeriod;
    previousFinancialYear: EnquiryActivityPeriod;
}

export interface EnquiryActivityPeriod {
    enquiryActivityGrouping: EnquiryActivityGrouping[];
    summary: EnquiryActivitySummary;
}

export interface EnquiryActivityGrouping {
    group: string;
    newEnquiries: number;
    followups: number;
}

export interface EnquiryActivitySummary {
    lastPeriodNewEnquiries: number;
    previousPeriodNewEnquiries: number;
    lastPeriodFollowups: number;
    previousPeriodFollowups: number;
}

export interface EnquiryActivityByPeriodQueryVars {
    entityType?: number | null;
    entityGuid?: string | null;
}

export const EnquiryActivityByPeriodQuery = gql`
    query GetEnquiryActivityByPeriod(
        $entityType: Int
        $entityGuid: String
    ) {
        statistics {
            lead {
                enquiryActivityByPeriod(entityType: $entityType, entityGuid: $entityGuid) {

                    lastFinancialYear {
                        enquiryActivityGrouping {
                            group
                            newEnquiries
                            followups
                        }
                        summary {
                            lastPeriodNewEnquiries
                            previousPeriodNewEnquiries
                            lastPeriodFollowups
                            previousPeriodFollowups
                        }
                    }

                    lastMonth {
                        enquiryActivityGrouping {
                            group
                            newEnquiries
                            followups
                        }
                        summary {
                            lastPeriodNewEnquiries
                            previousPeriodNewEnquiries
                            lastPeriodFollowups
                            previousPeriodFollowups
                        }
                    }

                    previousMonth {
                        enquiryActivityGrouping {
                            group
                            newEnquiries
                            followups
                        }
                        summary {
                            lastPeriodNewEnquiries
                            previousPeriodNewEnquiries
                            lastPeriodFollowups
                            previousPeriodFollowups
                        }
                    }

                    previousFinancialYear {
                        enquiryActivityGrouping {
                            group
                            newEnquiries
                            followups
                        }
                        summary {
                            lastPeriodNewEnquiries
                            previousPeriodNewEnquiries
                            lastPeriodFollowups
                            previousPeriodFollowups
                        }
                    }

                }
            }
        }
    }
`;
